import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { learningKeys } from '@/entities/learning';
import { updateLearningReminders } from '@/shared/api/learning';
import { ErrorMessage } from '@/shared/ui';

import styles from './BudgetForm.module.css';

export interface ReminderToggleProps {
  enabled: boolean;
  emailVerified: boolean;
}

/**
 * Turns the evening email for a day without study on or off. Saved at once;
 * an unconfirmed address is pointed out, because no reminder reaches it.
 */
export function ReminderToggle({ enabled, emailVerified }: ReminderToggleProps): React.JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [on, setOn] = useState(enabled);
  const hintId = useId();

  const mutation = useMutation({
    mutationFn: (next: boolean) => updateLearningReminders(next),
    onSuccess: async (stored) => {
      setOn(stored.remind_by_email);
      await queryClient.invalidateQueries({ queryKey: learningKeys.all });
    },
    onError: () => {
      setOn(enabled);
    },
  });

  return (
    <div className={styles.form}>
      <label className={styles.toggle}>
        <input
          type="checkbox"
          role="switch"
          aria-describedby={hintId}
          checked={on}
          disabled={mutation.isPending}
          onChange={(event) => {
            const { checked } = event.target;
            setOn(checked);
            mutation.mutate(checked);
          }}
        />
        <span>
          <span className={styles.toggleTitle}>{t('budget.remind')}</span>
          <span id={hintId} className={styles.hint}>
            {t('budget.remindHint')}
          </span>
        </span>
      </label>
      {on && !emailVerified && <p className={styles.warning}>{t('budget.remindUnverified')}</p>}
      {mutation.isError && <ErrorMessage error={mutation.error} />}
    </div>
  );
}
